import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useI18n } from "@/context/I18nContext";
import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";
import type { LocalizationKey } from "@/lib/api/localization";

export interface LocalizationCardProps {
  item: LocalizationKey;
  onPress: (item: LocalizationKey) => void;
}

/**
 * A single key card in the localization 2-column grid.
 *
 * Shows: key slug, Completed/Pending badge (top-right), relative last-edited
 * time, absolute timestamp, "More Info" button.
 * Matches Figma nodes for the redesigned card layout.
 */
export function LocalizationCard({ item, onPress }: LocalizationCardProps) {
  const { t, locale } = useI18n();

  const relativeTime = item.lastEditedAt
    ? formatRelativeTime(item.lastEditedAt, locale as any, t)
    : "—";

  const { date, time } = item.lastEditedAt
    ? formatSplitDateTime(item.lastEditedAt, locale as any)
    : { date: "—", time: "" };

  return (
    <View
      className="flex-1 border border-stroke-50 rounded-lg bg-white overflow-hidden"
      testID="localization-card"
    >
      {/* Text block + badge */}
      <View className="px-6 pt-4 gap-0.5">
        {/* Key slug + status badge row */}
        <View className="flex-row items-start justify-between gap-2">
          <AppText
            weight="semibold"
            className="flex-1 text-lg leading-[1.35] text-dark-text-50"
            numberOfLines={1}
          >
            {item.key}
          </AppText>
          <Badge
            label={
              item.isComplete
                ? "staff.localization.card.completed"
                : "staff.localization.card.pending"
            }
            variant={item.isComplete ? "filled-success" : "filled-warning"}
          />
        </View>
        <View className="gap-1 items-start">
          <AppText weight="medium" className="text-base text-supporting-text">
            {`${t("staff.localization.card.lastEdited")} ${relativeTime}`}
          </AppText>
          <View className="flex-row items-center gap-1">
            <AppText className="text-sm text-supporting-text-40">{date}</AppText>
            {!!time && (
              <>
                <View className="w-1 h-1 rounded-full bg-stroke-30" />
                <AppText className="text-sm text-supporting-text-40">{time}</AppText>
              </>
            )}
          </View>
        </View>
      </View>

      {/* More Info button */}
      <View className="px-6 pb-4 pt-6 items-start">
        <Button
          label="staff.localization.card.moreInfo"
          variant="secondary"
          onPress={() => onPress(item)}
          labelClassName="text-primary"
        />
      </View>
    </View>
  );
}
